<template>
    <report
        :report="report"
        :versions="versions"
        :loading="loading"
        :refreshing="refreshing"
        @reload="reload"
        @load-version="load_version"
    >
        <div class="lam-graph-container">
            <div class="uk-grid uk-grid-small uk-grid-width-1-2">
                <div>
                    <div class="uk-flex">
                        <div class="uk-margin-right">
                            <ul class="uk-subnav uk-subnav-line">
                                <li class="uk-disabled"><a href="">Staff</a></li>
                                <li :class="{'uk-active': !state.filter}"><a href @click.prevent="set_state_value('filter', '')">All</a></li>
                                <li
                                    v-for="filterable_role in filterable_roles"
                                    :key="filterable_role.id"
                                    :class="{'uk-active': state.filter === filterable_role.id}"
                                >
                                    <a href @click.prevent="set_state_value('filter', filterable_role.id)">
                                        {{ filterable_role.name }}
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <ul class="uk-subnav uk-subnav-line">
                                <li class="uk-disabled"><a href="">Studio</a></li>
                                <li :class="{'uk-active': !state.studio_id}"><a href @click.prevent="set_state_value('studio_id', '')">All</a></li>
                                <li
                                    v-for="studio in studios"
                                    :key="studio.id"
                                    :class="{'uk-active': state.studio_id === studio.id}"
                                >
                                    <a href @click.prevent="set_state_value('studio_id', studio.id)">{{ studio.name }}</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="uk-text-right">
                    <div class="uk-button-group lam-graph-filter">
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.mode === 'absolute'}"
                            @click.prevent="set_state_value('mode', 'absolute')"
                        >
                            Absolute
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.mode === 'relative'}"
                            @click.prevent="set_state_value('mode', 'relative')"
                        >
                            Relative
                        </base-button>
                    </div>

                    <div class="uk-button-group lam-graph-filter">
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'near-future'}"
                            @click.prevent="set_state_value('scope', 'near-future')"
                        >
                            This Week
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'next-week'}"
                            @click.prevent="set_state_value('scope', 'next-week')"
                        >
                            Next Week
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'two-weeks-out'}"
                            @click.prevent="set_state_value('scope', 'two-weeks-out')"
                        >
                            2 Weeks Out
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'three-weeks-out'}"
                            @click.prevent="set_state_value('scope', 'three-weeks-out')"
                        >
                            3 Weeks Out
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 'four-weeks-out'}"
                            @click.prevent="set_state_value('scope', 'four-weeks-out')"
                        >
                            4 Weeks Out
                        </base-button>
                    </div>
                </div>
            </div>
            <div class="uk-grid uk-grid-small uk-margin-bottom lam-graph">
                <div class="uk-width-1-1" style="position: relative;">
                    <weekly-workload-graph
                        ref="chart"
                        :chart-data="chart_data"
                        :display-mode="state.mode"
                        :y-max="ymax"
                        style="height: 600px;"
                        @click="route_to_record"
                    />
                </div>
            </div>
        </div>
    </report>
</template>

<script type="text/javascript">
import { draw } from "patternomaly"

import is_report from "./IsReport"
import handles_projects from "./HandlesProjects"

import Report from "./Report.vue"
import BaseButton from "../../ui/BaseButton"
import WeeklyWorkloadGraph from "./WeeklyWorkloadGraph.js"

export default {
    mixins: [is_report, handles_projects],
    components: {
        Report,
        BaseButton,
        WeeklyWorkloadGraph
    },
    computed: {
        chart_data () {
            const data = { labels: [], datasets: [] }
            const range_start = this.$moment().startOf("isoWeek")
            switch (this.state.scope) {
            case "next-week":
                range_start.add(1, "weeks")
                break
            case "two-weeks-out":
                range_start.add(2, "weeks")
                break
            case "three-weeks-out":
                range_start.add(3, "weeks")
                break
            case "four-weeks-out":
                range_start.add(4, "weeks")
                break
            }
            const time_off_dataset = {
                label: "OFF",
                backgroundColor: draw("diagonal", "#ddd"),
                categoryPercentage: 0.9,
                barPercentage: 0.8,
                data: []
            }

            if(this.report) {
                const project_datasets = {}
                const report_dataset = this.report.dataset[range_start.format("YYYY-MM-DD")] || []

                // const default_dataset = []
                Object.keys(report_dataset).forEach(initials => {
                    const user = this.raw_user_records.find(user => user.initials === initials)
                    if(user && (!this.state.filter || (this.state.filter === user.role)) && (!this.state.studio_id || (this.state.studio_id === user.studio_id))) {
                        data.labels.push(user.initials)
                        // default_dataset.push(0)

                        const days_off = this.off_days(user, range_start, this.$moment(range_start).add(6, "days"))
                        time_off_dataset.data.push((days_off * this.$root.applicationSettings.work_day_hours) || null)
                    }
                })

                data.labels.forEach((initials, index) => {
                    const user = this.raw_user_records.find(user => user.initials === initials)
                    if(user && (!this.state.filter || (this.state.filter === user.role)) && (!this.state.studio_id || (this.state.studio_id === user.studio_id))) {
                        const day_hours = this.$root.applicationSettings.work_day_hours * ((user.annual_utilisation_rate || 0) / 100)
                        const nb_days = 5

                        Object.keys(report_dataset[initials]).forEach(project_id => {
                            if(!project_datasets[project_id]) {
                                const project = this.entity_record("project", parseInt(project_id))
                                project_datasets[project_id] = {
                                    label: this.nibnut_filter("nibnut.smart-truncate", [project.number + (parseInt(project.number_suffix) ? `.${project.number_suffix}` : "") + " " + project.name, 30]),
                                    backgroundColor: project.hex_color,
                                    borderColor: "#ffffff",
                                    borderWidth: 1,
                                    categoryPercentage: 0.9,
                                    barPercentage: 0.8,
                                    data: new Array(data.labels.length).fill(0),
                                    project: project
                                }
                            }

                            let hours = report_dataset[initials][project_id] || 0
                            if(this.state.mode === "relative") {
                                hours = day_hours ? ((hours / (day_hours * nb_days)) * 100) : 0
                            }
                            project_datasets[project_id].data[index] = hours
                        })
                    }
                })
                data.datasets = Object.values(project_datasets)
                data.datasets.push(time_off_dataset)

                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.ymax = 0
                for(let loop = 0; loop < data.labels.length; loop++) {
                    let period_total = 0
                    data.datasets.forEach(dataset => {
                        period_total += parseFloat(dataset.data[loop]) || 0
                    })
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.ymax = Math.max(period_total, this.ymax)
                }
            }

            return data
        }
    },
    data () {
        return {
            report_id: "weekly_workload",

            default_state: {
                scope: "three-weeks-out",
                mode: "absolute",
                filter: "",
                user_id: 0,
                studio_id: 0,
                version_id: 0
            }
        }
    }
}
</script>
